/**
 * Formulario para programar una nueva entrevista del Hiring Manager
 * Permite seleccionar candidato, fecha, hora, modalidad y entrevistador
 */

import { CapaDeModal } from '@/shared/components/CapaDeModal'
import SelectBuscable from '@/shared/components/SelectBuscable'
import { comoEventoDeCambio } from '@/shared/lib/opcionesDeSelect'
import React, { useState, useEffect, useRef } from 'react'
import {
  X,
  AlertCircle,
  Loader2
} from 'lucide-react'
import { apiService } from '@/services/api'
import { useAuth } from '@/shared/hooks/useAuth'
import type {
  HiringManagerInterviewRegistration,
  HiringManagerInterviewFormErrors
} from '@/types/hiringManagerInterview'
import type { Candidate } from '@/services/api'
import type { ApprovedCandidateByArea } from '@/types/hiringManagerInterview'
import { filterOutMasterUsers } from '@/shared/lib/userFilters'
import { userHasPermission } from '@/shared/lib/roleUtils'
import { parseDateSafe } from '@/shared/lib/dateUtils'
import { entrevistadorSugerido } from '@/features/rh/lib/agendaDelSolicitante'
import {
  avisoDeCandidatosParaAgendar,
  avisoDeErrorAlCargar,
  type AvisoDeCarga,
} from '@/features/rh/lib/candidatosParaAgendar'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

interface ScheduleHiringManagerInterviewProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  preSelectedCandidateId?: string
}

export const ScheduleHiringManagerInterview: React.FC<ScheduleHiringManagerInterviewProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preSelectedCandidateId
}) => {
  const { user } = useAuth()

  /**
   * Quien agenda siendo solicitante es quien entrevista, así que el campo de
   * entrevistador sobra: se oculta y se toma su propio usuario.
   *
   * Se usa el permiso de crear solicitudes de personal porque es exactamente la
   * misma regla con la que el servidor decide quién puede ser entrevistador de
   * esta entrevista. Para quien agenda sin ese permiso —RH coordinando— el
   * campo se queda, porque ahí sí hay que decir quién entrevista.
   */
  const soySolicitante = userHasPermission(
    'rh.solicitudes_personal.base.create',
    user?.rbac?.effectivePermissions || [],
  )

  // Estado del formulario
  const [formData, setFormData] = useState<HiringManagerInterviewRegistration>({
    candidateId: preSelectedCandidateId || '',
    fechaEntrevista: '',
    horaEntrevista: '',
    modalidad: 'presencial',
    ubicacionLink: '',
    entrevistadorId: user?.id || '',
    notaArea: ''
  })

  const [errors, setErrors] = useState<HiringManagerInterviewFormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Listas para selects
  const [candidates, setCandidates] = useState<Candidate[]>([])
  /** Por qué la lista está vacía, cuando lo está. Null si hay candidatos. */
  const [avisoDeCandidatos, setAvisoDeCandidatos] = useState<AvisoDeCarga | null>(null)
  /**
   * De quién es la solicitud y cómo se llama el puesto, por candidato.
   *
   * Antes el nombre de la opción llevaba un guion colgando —«QA Siete Publico
   * -»— porque el listado nunca recibía el puesto. Y sin saber quién levantó la
   * solicitud, cuando RH agendaba en su nombre la entrevista quedaba a nombre
   * de RH (SRH-308).
   */
  const [vacantePorCandidato, setVacantePorCandidato] = useState<
    Record<string, { solicitanteId: string | null; puesto: string }>
  >({})
  /** Si la persona ya eligió entrevistador a mano, no se le vuelve a proponer otro. */
  const entrevistadorElegidoAMano = useRef(false)
  const [interviewers, setInterviewers] = useState<any[]>([])
  const [isLoadingData, setIsLoadingData] = useState(true)

  useEffect(() => {
    if (isOpen) {
      loadData()
      // Si hay un candidato preseleccionado, configurarlo
      if (preSelectedCandidateId) {
        setFormData(prev => ({ ...prev, candidateId: preSelectedCandidateId }))
      }
    }
  }, [isOpen, preSelectedCandidateId])

  /**
   * La sesión puede resolverse después del primer render, y entonces el estado
   * inicial se quedó con el entrevistador vacío. Con el campo oculto eso deja
   * un error que no se puede corregir desde la pantalla, así que se rellena en
   * cuanto hay usuario.
   */
  useEffect(() => {
    if (!user?.id) return
    setFormData(prev =>
      prev.entrevistadorId ? prev : { ...prev, entrevistadorId: user.id },
    )
  }, [user?.id])

  /**
   * Quien agenda en nombre del solicitante no es quien va a entrevistar. Se
   * propone al solicitante de esa vacante; si la persona ya eligió a alguien
   * a mano, se respeta.
   */
  useEffect(() => {
    if (entrevistadorElegidoAMano.current) return
    const solicitanteId = vacantePorCandidato[formData.candidateId]?.solicitanteId
    if (!formData.candidateId || !solicitanteId) return

    const propuesto = entrevistadorSugerido({
      solicitanteId,
      usuarioId: user?.id,
      entrevistadoresDisponibles: interviewers,
    })
    setFormData(prev =>
      propuesto && prev.entrevistadorId !== propuesto
        ? { ...prev, entrevistadorId: propuesto }
        : prev,
    )
  }, [formData.candidateId, vacantePorCandidato, interviewers, user?.id])

  const loadData = async () => {
    setIsLoadingData(true)
    try {
      // Cargar candidatos aprobados por RH del área
      const candidatesResponse = await fetch(
        `${apiService['baseUrl']}/candidates/hr-interview/approved-by-area`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      )

      if (candidatesResponse.ok) {
        const approvedCandidates = await candidatesResponse.json()
        // Mapear la respuesta al formato esperado de Candidate
        const candidatesData = approvedCandidates.map((approved: any) => ({
          id: approved.candidateId,
          fullName: approved.candidateName,
          email: approved.candidateEmail,
          phone: approved.candidatePhone,
          status: 'pre_seleccionado' as const
        }))
        setCandidates(candidatesData)
        setVacantePorCandidato(
          Object.fromEntries(
            (approvedCandidates as ApprovedCandidateByArea[]).map((approved) => [
              approved.candidateId,
              {
                solicitanteId: approved.solicitanteId ?? null,
                puesto: approved.jobRequestTitle ?? '',
              },
            ]),
          ),
        )
        setAvisoDeCandidatos(
          avisoDeCandidatosParaAgendar({ status: 200, total: candidatesData.length }),
        )
      } else {
        /**
         * Antes este caso no existía: sin `else`, un 403 se tragaba y la lista
         * se quedaba vacía, igual que si no hubiera nadie esperando. Quien lo
         * veía no podía distinguir «no tienes permiso» de «no hay candidatos».
         */
        setCandidates([])
        setAvisoDeCandidatos(
          avisoDeCandidatosParaAgendar({
            status: candidatesResponse.status,
            total: 0,
          }),
        )
      }

      // Cargar entrevistadores
      const usersResponse = await fetch(
        `${apiService['baseUrl']}/hiring-manager-interviews/interviewers`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      )

      if (usersResponse.ok) {
        const usersData = await usersResponse.json()
        const usersNoMaster = filterOutMasterUsers(usersData)
        const jefes = usersNoMaster.filter((u: any) => u.isActive !== false)
        setInterviewers(jefes)
      } else if (user?.id) {
        setInterviewers([
          {
            id: user.id,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
          },
        ])
      }
    } catch (error) {
      console.error('Error loading data:', error)
      // La peticion ni llego a responder: tampoco es «no hay candidatos».
      setAvisoDeCandidatos(avisoDeErrorAlCargar(error))
      if (user?.id) {
        setInterviewers([
          {
            id: user.id,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
          },
        ])
      }
    } finally {
      setIsLoadingData(false)
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    if (name === 'entrevistadorId') entrevistadorElegidoAMano.current = true
    setFormData(prev => ({ ...prev, [name]: value }))

    // Limpiar error del campo
    if (errors[name as keyof HiringManagerInterviewFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }

    // Si cambia la modalidad, limpiar ubicacionLink
    if (name === 'modalidad') {
      setFormData(prev => ({ ...prev, ubicacionLink: '' }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: HiringManagerInterviewFormErrors = {}

    if (!formData.candidateId) {
      newErrors.candidateId = 'Selecciona un candidato'
    }

    if (!formData.fechaEntrevista) {
      newErrors.fechaEntrevista = 'La fecha es requerida'
    } else {
      const selectedDate = parseDateSafe(formData.fechaEntrevista)
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      if (selectedDate && selectedDate < today) {
        newErrors.fechaEntrevista = 'La fecha no puede ser en el pasado'
      }
    }

    if (!formData.horaEntrevista) {
      newErrors.horaEntrevista = 'La hora es requerida'
    }

    if (!formData.modalidad) {
      newErrors.modalidad = 'Selecciona una modalidad'
    }

    if (formData.modalidad === 'virtual' && !formData.ubicacionLink) {
      newErrors.ubicacionLink = 'El link de la reunión es requerido para entrevistas virtuales'
    }

    if (!formData.entrevistadorId) {
      newErrors.entrevistadorId = 'Selecciona un entrevistador'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const response = await fetch(
        `${apiService['baseUrl']}/hiring-manager-interviews`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify(formData)
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Error al crear la entrevista')
      }

      // Éxito
      onSuccess()
      handleClose()
    } catch (errorCrudo: unknown) {
      const error = errorCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error creating interview:', error)
      setSubmitError(error.message || 'Error al crear la entrevista')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      candidateId: '',
      fechaEntrevista: '',
      horaEntrevista: '',
      modalidad: 'presencial',
      ubicacionLink: '',
      entrevistadorId: user?.id || '',
      notaArea: ''
    })
    setErrors({})
    setSubmitError(null)
    // La próxima vez que se abra se vuelve a proponer al solicitante.
    entrevistadorElegidoAMano.current = false
    onClose()
  }

  if (!isOpen) return null

  return (
    <CapaDeModal onClickFondo={handleClose}>
        <div className="w-full max-w-2xl overflow-hidden text-left bg-white shadow-xl rounded-lg">
          {/* Header */}
          <div className="sticky top-0 bg-white px-6 py-4 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Programar Entrevista
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Entrevista del Solicitante (Jefe de Departamento)
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
              disabled={isSubmitting}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4">
            {/* Error general */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-900">Error</h4>
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              </div>
            )}

            {isLoadingData ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              <>
                {/* Candidato */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Candidato *
                  </label>
                  <SelectBuscable
                    variante="formulario"
                    name="candidateId"
                    value={formData.candidateId}
                    onChange={(valor) => handleChange(comoEventoDeCambio('candidateId', valor))}
                    disabled={!!preSelectedCandidateId || isSubmitting}
                    error={Boolean(errors.candidateId)}
                    placeholder="Seleccionar candidato..."
                    placeholderDeBusqueda="Buscar candidato..."
                    opciones={candidates.map((candidate) => ({
                      value: String(candidate.id),
                      label: `${candidate.fullName}${
                        vacantePorCandidato[candidate.id]?.puesto
                          ? ` - ${vacantePorCandidato[candidate.id].puesto}`
                          : ''
                      }`,
                    }))}
                  />
                  {errors.candidateId && (
                    <p className="mt-1 text-xs text-red-600">{errors.candidateId}</p>
                  )}
                  {avisoDeCandidatos && (
                    <p
                      className={`mt-1 text-xs ${
                        avisoDeCandidatos.tono === 'error'
                          ? 'text-red-600'
                          : 'text-yellow-600'
                      }`}
                    >
                      {avisoDeCandidatos.texto}
                    </p>
                  )}
                </div>

                {/* Fecha y Hora */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha *
                    </label>
                    <input
                      type="date"
                      name="fechaEntrevista"
                      value={formData.fechaEntrevista}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={`w-full px-3 py-2 bg-[#F5F7F8] border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.fechaEntrevista ? 'border-red-300' : 'border-transparent'
                        }`}
                    />
                    {errors.fechaEntrevista && (
                      <p className="mt-1 text-xs text-red-600">{errors.fechaEntrevista}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hora *
                    </label>
                    <input
                      type="time"
                      name="horaEntrevista"
                      value={formData.horaEntrevista}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={`w-full px-3 py-2 bg-[#F5F7F8] border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.horaEntrevista ? 'border-red-300' : 'border-transparent'
                        }`}
                    />
                    {errors.horaEntrevista && (
                      <p className="mt-1 text-xs text-red-600">{errors.horaEntrevista}</p>
                    )}
                  </div>
                </div>

                {/* Modalidad y Ubicación comparten renglón: la segunda depende
                    de la primera, y leerlas juntas evita capturar un enlace
                    creyendo que la entrevista es presencial. */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Modalidad *
                    </label>
                    <SelectBuscable
                      variante="formulario"
                      name="modalidad"
                      value={formData.modalidad}
                      onChange={(valor) => handleChange(comoEventoDeCambio('modalidad', valor))}
                      disabled={isSubmitting}
                      error={Boolean(errors.modalidad)}
                      canDelete={false}
                      placeholderDeBusqueda="Buscar modalidad..."
                      opciones={[
                        { value: 'presencial', label: 'Presencial' },
                        { value: 'virtual', label: 'Virtual' },
                      ]}
                    />
                    {errors.modalidad && (
                      <p className="mt-1 text-xs text-red-600">{errors.modalidad}</p>
                    )}
                  </div>

                  {/* Ubicación / Link */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.modalidad === 'virtual' ? 'Link de la Reunión *' : 'Ubicación *'}
                    </label>
                    <input
                      type="text"
                      name="ubicacionLink"
                      value={formData.ubicacionLink}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      placeholder={
                        formData.modalidad === 'virtual'
                          ? 'https://zoom.us/j/...'
                          : 'Sala de juntas, edificio principal'
                      }
                      className={`w-full px-3 py-2 bg-[#F5F7F8] border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.ubicacionLink ? 'border-red-300' : 'border-transparent'
                        }`}
                    />
                    {errors.ubicacionLink && (
                      <p className="mt-1 text-xs text-red-600">{errors.ubicacionLink}</p>
                    )}
                  </div>
                </div>

                {/* Entrevistador: oculto cuando quien entrevista es quien agenda. Tener
                    el permiso de solicitante no basta: si se agenda la vacante de
                    otra persona, el entrevistador propuesto es esa persona y
                    decir «tú» sería mentira. */}
                {soySolicitante && formData.entrevistadorId === user?.id ? (
                  <p className="text-sm text-gray-500">
                    Tú serás el entrevistador de esta entrevista.
                  </p>
                ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Entrevistador *
                  </label>
                  <SelectBuscable
                    variante="formulario"
                    name="entrevistadorId"
                    value={formData.entrevistadorId}
                    onChange={(valor) => handleChange(comoEventoDeCambio('entrevistadorId', valor))}
                    disabled={isSubmitting}
                    error={Boolean(errors.entrevistadorId)}
                    placeholder="Seleccionar entrevistador..."
                    placeholderDeBusqueda="Buscar entrevistador..."
                    opciones={interviewers.map((interviewer) => ({
                      value: String(interviewer.id),
                      label: `${interviewer.firstName} ${interviewer.lastName} - ${interviewer.email}`,
                    }))}
                  />
                  {errors.entrevistadorId && (
                    <p className="mt-1 text-xs text-red-600">{errors.entrevistadorId}</p>
                  )}
                </div>
                )}

                {/* Notas del Área */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas del Departamento/Área (opcional)
                  </label>
                  <textarea
                    name="notaArea"
                    value={formData.notaArea}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    rows={3}
                    placeholder="Puntos clave a evaluar, información adicional del candidato, etc."
                    className="w-full px-3 py-2 bg-[#F5F7F8] border border-transparent rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-4 mt-6">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isLoadingData || candidates.length === 0}
                className={estiloDeBoton('primario')}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Programando...
                  </>
                ) : (
                  'Programar Entrevista'
                )}
              </button>
            </div>
          </form>
        </div>
    </CapaDeModal>
  )
}
